import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, PhoneCall, CheckCircle2, XCircle, Voicemail, PhoneOff, Ban, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { OutboundStore } from '../../lib/outboundStore';

const CALL_RESULTS = [
  { key: 'connected',      label: 'Connected',      icon: CheckCircle2, tone: 'emerald' },
  { key: 'left_vm',        label: 'Left VM',        icon: Voicemail,    tone: 'sky' },
  { key: 'no_answer',      label: 'No Answer',      icon: PhoneOff,     tone: 'neutral' },
  { key: 'wrong_number',   label: 'Wrong Number',   icon: XCircle,      tone: 'amber' },
  { key: 'bad_number',     label: 'Bad Number',     icon: Ban,          tone: 'red' },
  { key: 'not_interested', label: 'Not Interested', icon: XCircle,      tone: 'red' },
  { key: 'callback',       label: 'Requested Callback', icon: PhoneCall, tone: 'gold' },
];

const TONE_CLS = {
  emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300',
  sky:     'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300',
  neutral: 'border-neutral-200 bg-neutral-50 text-neutral-700 hover:border-neutral-300',
  amber:   'border-amber-200 bg-amber-50 text-amber-800 hover:border-amber-300',
  red:     'border-red-200 bg-red-50 text-red-700 hover:border-red-300',
  gold:    'border-[hsl(var(--kf-gold))]/40 bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] hover:border-[hsl(var(--kf-gold))]',
};

const STATUS_TRANSITIONS = [
  { key: 'new',            label: 'New' },
  { key: 'attempted',      label: 'Attempted' },
  { key: 'contacted',      label: 'Contacted' },
  { key: 'follow_up',      label: 'Follow-Up' },
  { key: 'appointment',    label: 'Appointment' },
  { key: 'hot',            label: 'Hot · promote to CRM' },
  { key: 'not_interested', label: 'Not Interested' },
];

/**
 * LogCallModal — pops after the LO taps the dial button. Captures the
 * call outcome + free-text notes + optional follow-up + status change,
 * then POSTs to /api/outbound/leads/{id}/log-call which increments
 * outboundAttempts and appends a note in one shot.
 *
 * Can also be launched from Focus Mode — behaves identically.
 */
export default function LogCallModal({ lead, onClose, onLogged }) {
  const [result, setResult] = useState('');
  const [notes, setNotes] = useState('');
  const [nextStatus, setNextStatus] = useState('');
  const [followUpAt, setFollowUpAt] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (lead) {
      setResult('');
      setNotes('');
      setNextStatus('');
      setFollowUpAt('');
    }
  }, [lead?.id]);

  if (!lead) return null;

  const handleSubmit = async () => {
    setBusy(true);
    try {
      const payload = {};
      if (result) payload.result = result;
      if (notes.trim()) payload.notes = notes.trim();
      if (nextStatus) payload.outbound_status = nextStatus;
      if (followUpAt) payload.next_follow_up_at = new Date(followUpAt).toISOString();
      const r = await OutboundStore.logCall(lead.id, payload);
      if (!r) throw new Error('log call failed');
      if (nextStatus === 'hot') {
        toast.success('🔥 Promoted to CRM — now in My Leads', { duration: 3500 });
      } else {
        toast.success('Call logged');
      }
      if (onLogged) onLogged(r);
      onClose();
    } catch (e) {
      toast.error('Failed to log the call');
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div
      data-testid="log-call-modal"
      className="fixed inset-0 z-[85] bg-neutral-950/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={() => !busy && onClose()}
    >
      <div
        className="w-full max-w-lg rounded-2xl border border-neutral-800 bg-white shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b hairline px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-full bg-neutral-900 text-white font-bold text-[11px] flex items-center justify-center">
              {lead.initials || '?'}
            </div>
            <div>
              <div className="text-[13.5px] font-bold text-neutral-900">Log call · {lead.name}</div>
              <div className="text-[11px] text-neutral-500 tabular-nums">
                {lead.phone || 'No number'} · attempt #{(lead.outboundAttempts || 0) + 1}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-500" data-testid="log-call-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Result buttons */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-2">Call result</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {CALL_RESULTS.map((r) => {
                const Icon = r.icon;
                const active = result === r.key;
                return (
                  <button
                    key={r.key}
                    data-testid={`log-call-result-${r.key}`}
                    onClick={() => setResult(r.key)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11.5px] font-semibold transition',
                      TONE_CLS[r.tone],
                      active ? 'ring-2 ring-neutral-900 ring-offset-1' : ''
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="truncate">{r.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Notes</div>
            <textarea
              data-testid="log-call-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="What did you learn on this call?"
              className="w-full rounded-lg border hairline bg-white px-3 py-2 text-[13px] placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/10 resize-none"
            />
          </div>

          {/* Status transition + follow-up */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Update status</div>
              <select
                data-testid="log-call-next-status"
                value={nextStatus}
                onChange={(e) => setNextStatus(e.target.value)}
                className="w-full rounded-lg border hairline bg-white px-3 py-2 text-[13px]"
              >
                <option value="">— No change —</option>
                {STATUS_TRANSITIONS.map((s) => (
                  <option key={s.key} value={s.key}>{s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5">Next follow-up</div>
              <input
                data-testid="log-call-follow-up"
                type="datetime-local"
                value={followUpAt}
                onChange={(e) => setFollowUpAt(e.target.value)}
                className="w-full rounded-lg border hairline bg-white px-3 py-2 text-[13px]"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t hairline bg-neutral-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-lg border hairline px-3.5 py-2 text-[12.5px] font-semibold text-neutral-700 hover:bg-white"
          >
            Cancel
          </button>
          <button
            data-testid="log-call-submit"
            onClick={handleSubmit}
            disabled={busy}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 text-white px-4 py-2 text-[12.5px] font-semibold',
              busy && 'opacity-50 cursor-not-allowed'
            )}
          >
            {busy ? 'Saving…' : (<>Log call <ArrowRight className="h-3.5 w-3.5" /></>)}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
